/**
 * Password plus a second factor (authenticator code or recovery code), for
 * the MFA actions that need re-authentication (tracker item 63): turning it
 * off, new recovery codes, and an admin's reset of another user. Calls
 * `onSubmit` with the values; shows `error` from the caller.
 */
import React, { useState } from 'react';
import { MfaReauth } from '../api/mfaTypes';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';

interface MfaReauthFormProps {
  /** id prefix for the inputs */
  idPrefix: string;
  /** Ask for a code too (the caller uses MFA) */
  needsFactor: boolean;
  submitLabel: string;
  busy: boolean;
  error: string | null;
  onSubmit: (reauth: MfaReauth) => void;
  onCancel?: () => void;
  /** Explanation above the fields */
  intro?: React.ReactNode;
  danger?: boolean;
}

export function MfaReauthForm({ idPrefix, needsFactor, submitLabel, busy, error, onSubmit, onCancel, intro, danger }: MfaReauthFormProps) {
  const [password, setPassword] = useState('');
  const [value, setValue] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!password) {
      setLocalError('Enter your password');
      return;
    }
    if (needsFactor && !value.trim()) {
      setLocalError(useRecovery ? 'Enter a recovery code' : 'Enter the 6-digit code from your app');
      return;
    }
    const factor = !needsFactor ? {} : useRecovery ? { recoveryCode: value.trim() } : { code: value.trim() };
    onSubmit({ password, ...factor });
  };

  const shown = localError ?? error;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-3">
      {intro && <div className="text-sm text-gray-600">{intro}</div>}
      {shown && <Toast kind="error">{shown}</Toast>}
      <div>
        <label htmlFor={`${idPrefix}-password`} className={labelClass}>Your password</label>
        <input
          id={`${idPrefix}-password`}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          className={inputClass}
        />
      </div>
      {needsFactor && (
        <div>
          <label htmlFor={`${idPrefix}-code`} className={labelClass}>
            {useRecovery ? 'Recovery code' : 'Code from your authenticator app'}
          </label>
          <input
            id={`${idPrefix}-code`}
            type="text"
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete={useRecovery ? 'off' : 'one-time-code'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            maxLength={useRecovery ? 13 : 7}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => {
              setUseRecovery((v) => !v);
              setValue('');
            }}
            className="mt-1 inline-flex min-h-[44px] sm:min-h-0 items-center text-sm text-blue-600 hover:text-blue-800"
          >
            {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code'}
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={busy}
          className={danger ? primaryButtonClass.replace('bg-blue-600 hover:bg-blue-700', 'bg-red-600 hover:bg-red-700') : primaryButtonClass}
        >
          {busy ? 'Working...' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={busy} className={secondaryButtonClass}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
